<template>
  <div id="app">
    <!-- Bootstrap-skärm medan vi hydrerar rollerna från /api/auth/me. -->
    <!-- Utan detta hinner router-guarden för /admin köra med tom roll-state. -->
    <div v-if="!isHydrated" class="app-bootstrap" aria-hidden="true">
      <q-spinner size="32px" color="primary" />
    </div>
    <template v-else>
      <nav-bar />
      <router-view />
      <cookie-banner
        @consent-accepted="onConsentAccepted"
        @consent-rejected="onConsentRejected"
      />
      <beta-announcement />
    </template>
  </div>
</template>

<script>
import NavBar from "@/components/navbar.vue";
import CookieBanner from "@/components/cookie-banner.vue";
import BetaAnnouncement from "@/components/beta-announcement.vue";
import { applyDarkMode, isDarkMode } from "@/utils/darkMode";
import { getConsent } from "@/utils/cookieConsent";
import {
  grantAnalyticsConsent,
  revokeAnalyticsConsent,
} from "@/utils/analyticsConsent";
import { hydrate, isHydrated } from "@/utils/auth";

export default {
  name: "App",
  components: {
    NavBar,
    CookieBanner,
    BetaAnnouncement,
  },
  computed: {
    isHydrated() {
      return isHydrated.value;
    },
  },
  created() {
    // Starta hydrationen så tidigt som möjligt - vi vill inte vänta tills
    // App är monterad innan vi pingar /me, då tar det extra render-tid.
    hydrate();
    // Applicera dark mode INNAN första render - annars hinner Quasar-
    // komponenter (q-dialog, q-input osv) renderas i ljust läge en frame
    // innan vi flippar Quasars egna Dark.state.
    applyDarkMode(isDarkMode.value);
  },
  mounted() {
    // Consent Mode v2 är initialiserat i index.html med default "denied".
    // GA skickar cookieless pings för anonyma counts oavsett, så det räcker
    // att uppdatera consent när användaren har gjort ett aktivt val tidigare.
    const stored = getConsent();
    if (stored === "accepted") {
      grantAnalyticsConsent();
    } else if (stored === "rejected") {
      revokeAnalyticsConsent();
    }
    // Ingen tidigare val → vi låter default "denied" stå tills bannern
    // ger oss ett svar (cookieless pings sker fortfarande).
  },
  methods: {
    onConsentAccepted() {
      grantAnalyticsConsent();
    },
    onConsentRejected() {
      revokeAnalyticsConsent();
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Inter:wght@300;400;500;600&display=swap");

:root {
  --color-bg: #f5f0e8;
  --color-border: #e0d9d0;
  --color-border-light: #ccc;
  --color-text: #1a1a1a;
  --color-text-secondary: #555;
  --color-text-muted: #888;
  --color-text-faint: #aaa;
  --color-accent: #cc1100;
  --color-image-placeholder: #d0c9c0;
  --color-skeleton-base: #e8e2d9;
  --color-skeleton-highlight: #f0ece4;
  --color-input-text: #1a1a1a;
  --color-meta-value: #333;
  color-scheme: light;
}

html.dark {
  --color-bg: #181818;
  --color-border: #2e2e2e;
  --color-border-light: #444;
  --color-text: #f0ece4;
  --color-text-secondary: #bbb;
  --color-text-muted: #888;
  --color-text-faint: #555;
  --color-accent: #ff3311;
  --color-image-placeholder: #2a2a2a;
  --color-skeleton-base: #2a2a2a;
  --color-skeleton-highlight: #363636;
  --color-input-text: #f0ece4;
  --color-meta-value: #ccc;
  color-scheme: dark;

  /* Quasars primary (knappar/checkbox/input-focus) ska kontrastera mot
     bakgrunden i dark mode också. Light mode kör #1a1a1a från brand-config
     - här invertarar vi den till off-white så Quasar-komponenter inte
     försvinner mot den mörka bakgrunden. */
  --q-primary: #f0ece4;
}

*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body {
  background-color: var(--color-bg);
  color: var(--color-text);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background-color 0.2s ease, color 0.2s ease;
}

#app {
  min-height: 100vh;
}

a {
  text-decoration: none;
  color: inherit;
}

.app-bootstrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg);
}
</style>
